/**
 * Εισπράξεις — the cross-policy collection worklist (review pass 2).
 *
 * The daily task this serves: work through everything unpaid, most overdue first,
 * phone in hand. Rows default to pending+overdue because paid instalments are history,
 * not work; recording a payment happens inline so the operator never leaves the list.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Phone } from 'lucide-react';
import { today, type InstallmentDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { EnumBadge, useEnumOptions } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { PolicyRef } from '@/components/common/policy-ref';
import { FilterBar, type FilterDescriptor } from '@/components/filters/filter-bar';
import { RequiresConnection } from '@/components/offline/offline-indicator';
import { ApiRequestError } from '@/lib/api/client';
import { useInstallmentsList, useRecordInstallmentPayment } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';

type CollectionRow = InstallmentDto & { clientName?: string; clientPhone?: string | null };

export function CollectionsPage() {
  const { t, money, date } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();

  const list = useListState();
  const { data, isLoading, isFetching, error, refetch } = useInstallmentsList(list.params);
  const [paying, setPaying] = React.useState<CollectionRow | null>(null);

  const outstanding = (data?.meta as { outstanding?: string } | undefined)?.outstanding;

  const columns = React.useMemo<DataColumn<CollectionRow>[]>(
    () => [
      {
        id: 'clientName',
        header: t('client.one'),
        meta: { headerLabel: t('client.one') },
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-medium">{row.original.clientName}</p>
            {row.original.clientPhone && (
              <a
                href={`tel:${row.original.clientPhone}`}
                onClick={(event) => event.stopPropagation()}
                className="text-muted-foreground hover:text-primary inline-flex items-center gap-1 text-xs tabular hover:underline"
              >
                <Phone className="size-3" />
                {row.original.clientPhone}
              </a>
            )}
          </div>
        ),
      },
      {
        id: 'policyNumber',
        header: t('policy.one'),
        meta: { hideBelow: 'md', headerLabel: t('policy.one') },
        cell: ({ row }) => (
          <PolicyRef policyNumber={row.original.policyNumber ?? ''} plates={row.original.plates} />
        ),
      },
      {
        id: 'installment',
        header: t('collections.installment'),
        meta: { align: 'center', numeric: true, hideBelow: 'lg', headerLabel: t('collections.installment') },
        cell: ({ row }) => `${row.original.installmentNumber}/${row.original.totalInstallments}`,
      },
      {
        id: 'dueDate',
        header: t('policy.dueDate'),
        meta: { numeric: true, headerLabel: t('policy.dueDate') },
        cell: ({ row }) => (
          <span className="flex items-center gap-2 whitespace-nowrap">
            {date(row.original.dueDate)}
            {(row.original.daysOverdue ?? 0) > 0 && (
              <Badge tone="critical" size="sm" className="tabular">
                +{row.original.daysOverdue}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'amount',
        header: t('policy.amount'),
        meta: { align: 'right', numeric: true, headerLabel: t('policy.amount') },
        cell: ({ row }) => <span className="font-semibold">{money(row.original.amount)}</span>,
      },
      {
        id: 'status',
        header: t('policy.status'),
        meta: { hideBelow: 'md', headerLabel: t('policy.status') },
        cell: ({ row }) => <EnumBadge enum="installmentStatus" value={row.original.status} size="sm" />,
      },
      {
        id: 'actions',
        header: '',
        meta: { width: '11rem', align: 'right' },
        cell: ({ row }) =>
          can('policy:write') && (
            <div onClick={(event) => event.stopPropagation()}>
              <RequiresConnection reasonKey="offline.blocked.financial">
                <Button variant="outline" size="sm" onClick={() => setPaying(row.original)}>
                  {t('policy.recordPayment')}
                </Button>
              </RequiresConnection>
            </div>
          ),
      },
    ],
    [t, money, date, can],
  );

  /*
   * One declaration, two homes: a filter naming a `column` is rendered in the table's
   * filter row under that column, the rest stay in the toolbar, and the narrow-screen
   * popover keeps all of them.
   */
  const filters: FilterDescriptor[] = [
    { key: 'status', type: 'enum', enum: 'installmentStatus', label: t('policy.status'), multiple: true, column: 'status' },
    { key: 'dueFrom', type: 'date', label: t('common.from'), column: 'dueDate' },
    { key: 'dueTo', type: 'date', label: t('common.to') },
  ];

  return (
    <>
      <PageHeader title={t('collections.title')} description={t('collections.subtitle')} />

      <FilterBar
        className="mb-5"
        listState={list}
        inline="unbound"
        searchPlaceholder={`${t('client.one')} / ${t('policy.number')}`}
        filters={filters}
      />

      <DataTable
        columns={columns}
        rows={data?.data as CollectionRow[] | undefined}
        total={data?.meta.total}
        listState={list}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={refetch}
        getRowId={(row) => row.id}
        onRowClick={(row) => navigate(`/policies/${row.policyId}`)}
        rowClassName={(row) => ((row.daysOverdue ?? 0) > 0 ? 'row-tint-critical' : undefined)}
        footer={
          outstanding && (
            <span className="text-muted-foreground">
              {t('collections.outstanding')}{' '}
              <span className="text-foreground font-semibold tabular">{money(outstanding)}</span>
            </span>
          )
        }
      />

      {paying && <PaymentDialog row={paying} onOpenChange={(open) => !open && setPaying(null)} />}
    </>
  );
}

/** Inline payment recording — pre-filled with the instalment's own figures. */
function PaymentDialog({
  row,
  onOpenChange,
}: {
  row: CollectionRow;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, money } = useI18n();
  const methodOptions = useEnumOptions('paymentMethod');
  const recordPayment = useRecordInstallmentPayment(row.policyId);

  const [amount, setAmount] = React.useState(row.amount);
  const [paymentDate, setPaymentDate] = React.useState(() => today());
  const [method, setMethod] = React.useState('cash');

  const submit = async () => {
    try {
      const result = await recordPayment.mutateAsync({
        installmentId: row.id,
        paidAmount: amount,
        paymentDate,
        paymentMethod: method,
      });
      toast.success(
        result.renewed
          ? `${row.policyNumber} — ${t('policy.markPaidTitle')}`
          : `${row.clientName} · ${money(amount)}`,
      );
      onOpenChange(false);
    } catch (caught) {
      toast.error(caught instanceof ApiRequestError ? caught.message : t('error.generic'));
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('policy.recordPayment')}</DialogTitle>
          <DialogDescription>
            {row.clientName} · {row.policyNumber} · {t('collections.installment')}{' '}
            {row.installmentNumber}/{row.totalInstallments}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pay-amount">{t('policy.amount')}</Label>
              <Input
                id="pay-amount"
                type="number"
                step="0.01"
                className="tabular"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-date">{t('policy.dueDate')}</Label>
              <Input
                id="pay-date"
                type="date"
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('policy.paymentMethod')}</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {methodOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={recordPayment.isPending}>
            {t('action.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
